import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Clickable from 'components/Common/Clickable'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ModalContext from 'contexts/ModalContext'
import { partitionBy } from 'lib/array/arrayUtils'
import { type TransportConnection, type TransportOption, isFlightCardSavedItemData } from 'tripPlanner/api/types'
import useRefreshShortlistedFlights from './useRefreshShortlistedFlights'
import type { RefreshedFlight } from './useRefreshShortlistedFlights'
import useBookShortlistedFlight from './useBookShortlistedFlight'
import useViewBookedShortlistedFlight from './useViewBookedShortlistedFlight'
import ShortlistedFlightCard from './ShortlistedFlightCard'
import { isBookedFlightOption } from './flightShortlistUtils'

const SectionContainer = styled.div`
  width: 100%;
  margin-top: ${rem(12)};
`

const HeaderClickable = styled(Clickable)`
  width: 100%;
`

const HeaderContainer = styled.div<{ $hasTopSpacing: boolean }>`
  margin-top: ${props => (props.$hasTopSpacing ? rem(12) : 0)};
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin-top: ${rem(12)};
`

const CountCircle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(22)};
  min-width: ${rem(22)};
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  color: ${props => props.theme.palette.highlight.primary.lightContrast};
`

const SkeletonSeparator = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin: 0;
  width: 100%;
`

function FlightCardSkeleton() {
  return (
    <Sheet border="light-foreground" borderRadius="L" padding={16}>
      <Group direction="vertical" gap={16} fullWidth>
        <Group direction="vertical" gap={4}>
          <LoadingBox height={16} width={120} borderRadius="S" />
          <LoadingBox height={12} width={220} borderRadius="S" />
        </Group>

        <SkeletonSeparator />

        <Group direction="horizontal" gap={16} verticalAlign="center" fullWidth>
          <LoadingBox height={32} width={32} borderRadius="round" />
          <Group direction="vertical" gap={4}>
            <LoadingBox height={16} width={110} borderRadius="S" />
            <LoadingBox height={12} width={80} borderRadius="S" />
          </Group>
          <Group direction="vertical" gap={4}>
            <LoadingBox height={14} width={60} borderRadius="S" />
            <LoadingBox height={12} width={50} borderRadius="S" />
          </Group>
          <LoadingBox height={14} width={50} borderRadius="S" />
        </Group>

        <SkeletonSeparator />

        <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between" fullWidth>
          <LoadingBox height={20} width={90} borderRadius="S" />
          <LoadingBox height={32} width={80} borderRadius="M" />
        </Group>
      </Group>
    </Sheet>
  )
}

function getShortlistedOptions(transport: TransportConnection): Array<TransportOption> {
  return transport.options.filter(o => isFlightCardSavedItemData(o.savedItemData))
}

interface Props {
  transport: TransportConnection
  onDeleteItem: (itemId: number) => void
}

function ShortlistedFlightsSection({ transport, onDeleteItem }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const [isExpanded, setIsExpanded] = useState(true)

  const shortlistedOptions = useMemo(
    () => getShortlistedOptions(transport),
    [transport],
  )
  const [bookedOptions, unbookedOptions] = useMemo(
    () => partitionBy(shortlistedOptions, isBookedFlightOption),
    [shortlistedOptions],
  )

  const { status: refreshStatus, refreshedFlights, refresh } = useRefreshShortlistedFlights()
  const { bookFlight, bookingItemId, searchingItemId } = useBookShortlistedFlight(showModal)
  const { viewBookedFlightDetails, viewUnbookedFlightDetails, viewingDetailsItemId } = useViewBookedShortlistedFlight(showModal)

  const hasAutoRefreshed = useRef(false)
  useEffect(() => {
    if (hasAutoRefreshed.current || unbookedOptions.length === 0) return
    hasAutoRefreshed.current = true
    refresh(unbookedOptions)
  }, [refresh, unbookedOptions])

  const isRefreshing = refreshStatus === 'refreshing'

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  const handleRefresh = useCallback(async() => {
    await refresh(unbookedOptions)
  }, [refresh, unbookedOptions])

  const handleDelete = useCallback((option: TransportOption) => {
    onDeleteItem(option.itemId)
  }, [onDeleteItem])

  const handleViewUnbookedDetails = useCallback((option: TransportOption, refreshedData?: RefreshedFlight) => {
    viewUnbookedFlightDetails(option, refreshedData, bookFlight)
  }, [viewUnbookedFlightDetails, bookFlight])

  const getRefreshedData = useCallback((option: TransportOption): RefreshedFlight | undefined => {
    return refreshedFlights.find(r => r.option.itemId === option.itemId)
  }, [refreshedFlights])

  const renderShortlistedFlightCard = useCallback((option: TransportOption) => (
    <ShortlistedFlightCard
      key={option.itemId}
      option={option}
      refreshedData={getRefreshedData(option)}
      onBookNow={bookFlight}
      onDelete={handleDelete}
      onRefresh={handleRefresh}
      isBooking={bookingItemId === option.itemId}
      isSearching={searchingItemId === option.itemId}
      onViewBookedDetails={viewBookedFlightDetails}
      onViewUnbookedDetails={handleViewUnbookedDetails}
      isViewingDetails={viewingDetailsItemId === option.itemId}
    />
  ), [
    bookingItemId,
    bookFlight,
    getRefreshedData,
    handleDelete,
    handleRefresh,
    handleViewUnbookedDetails,
    searchingItemId,
    viewBookedFlightDetails,
    viewingDetailsItemId,
  ])

  if (shortlistedOptions.length === 0) return null

  const hasBookedOptions = bookedOptions.length > 0
  const hasUnbookedOptions = unbookedOptions.length > 0
  const ToggleIcon = isExpanded ? LineAngleUpIcon : LineAngleDownIcon

  const headerLabel = intl.formatMessage({
    id: 'QnzwHKT3FGvP48bzGzPV',
    defaultMessage: 'Shortlisted flights',
    description: 'Header label for the shortlisted flights section in trip planner itinerary',
  })

  return (
    <SectionContainer>
      {hasBookedOptions && (
        <CardsContainer>
          {bookedOptions.map(renderShortlistedFlightCard)}
        </CardsContainer>
      )}

      {hasUnbookedOptions && <>
        <HeaderContainer $hasTopSpacing={hasBookedOptions}>
          <HeaderClickable onClick={toggleExpanded}>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <BodyText variant="medium" weight="bold">{headerLabel}</BodyText>
              <CountCircle>
                <Caption variant="smallalt" weight="bold">{unbookedOptions.length}</Caption>
              </CountCircle>
              <ToggleIcon size="S" />
            </Group>
          </HeaderClickable>
        </HeaderContainer>

        {isExpanded && (
          <CardsContainer>
            {isRefreshing && unbookedOptions.map(option => (
              <FlightCardSkeleton key={option.itemId} />
            ))}

            {!isRefreshing && unbookedOptions.map(renderShortlistedFlightCard)}
          </CardsContainer>
        )}
      </>}
    </SectionContainer>
  )
}

export default ShortlistedFlightsSection
